/**
 * apPortApply -- applying an AP Port Profile to APs, with a confirm and per-AP results
 * (OP#1937, 2026-10-19).
 *
 * WHY: "Apply to N APs" fired one live owgw push per AP immediately, with no preview and no
 * confirm, although EACH push takes that AP off the air for about 4 minutes. All the pushes
 * shared one mutation, so only the LAST AP's error was shown and "Applied." could appear after
 * earlier APs had failed. And saving a profile edit never reached the APs already using it, while
 * the page claimed it did. Now:
 *   * Apply opens a confirm that lists every AP and states the ~4 minute outage per AP;
 *   * the pushes run one AP at a time and every AP gets its own result row (applyToAps) --
 *     a failure is never hidden behind a later success;
 *   * after saving a profile that is in use, a banner says the APs do not have the change
 *     until it is applied (profileDriftBanner). Saving never pushes.
 *
 * Pure (no React, no fetch): unit-tested in apPortApply.test.ts (`npm run test:unit`).
 */

/** Approximate outage per AP while it takes a pushed port config (owgw push + reconnect). */
export const AP_PUSH_OUTAGE_MINUTES = 4;

export interface ApTarget {
  serial: string;
  name?: string | null;
}

export interface ApApplyResult {
  serial: string;
  name: string;
  ok: boolean;
  /** Server said the AP was already in the target state (nothing was pushed). */
  unchanged?: boolean;
  /** The server's reason when this AP failed (port-compatibility, owgw rejection, ...). */
  error?: string;
}

function label(t: ApTarget): string {
  const n = (t.name ?? "").trim();
  return n && n !== t.serial ? n : t.serial;
}

/** Confirm-dialog wording: names the profile, the AP count and the per-AP outage. */
export function applyConfirmText(profileName: string, count: number): { title: string; impact: string } {
  const aps = `${count} AP${count === 1 ? "" : "s"}`;
  return {
    title: `Apply "${profileName}" to ${aps}?`,
    impact:
      `Each AP is pushed a new port configuration and goes offline for about ` +
      `${AP_PUSH_OUTAGE_MINUTES} minutes while it applies it; every client and wired device on ` +
      `that AP drops. APs are pushed one at a time and each result is shown.`,
  };
}

/**
 * Apply to each AP IN TURN, never stopping on a failure, and return one result per AP in the
 * order given. `applyOne` performs the single-AP push (POST /devices/<serial>/port-profile) and
 * rejects with the server's error. `onResult` lets the UI render progress as each AP finishes.
 */
export async function applyToAps(
  targets: ApTarget[],
  applyOne: (serial: string) => Promise<{ changed?: boolean } | unknown>,
  onResult?: (r: ApApplyResult, done: number) => void,
): Promise<ApApplyResult[]> {
  const out: ApApplyResult[] = [];
  for (const t of targets) {
    let r: ApApplyResult;
    try {
      const res = (await applyOne(t.serial)) as { changed?: boolean } | undefined;
      r = { serial: t.serial, name: label(t), ok: true, unchanged: res?.changed === false };
    } catch (e) {
      const msg = e instanceof Error && e.message ? e.message : "Apply failed.";
      r = { serial: t.serial, name: label(t), ok: false, error: msg };
    }
    out.push(r);
    onResult?.(r, out.length);
  }
  return out;
}

/** One-line summary of a finished run; "partial"/"failed" whenever ANY AP failed. */
export function applySummary(results: ApApplyResult[]): { tone: "ok" | "partial" | "failed"; text: string } {
  const failed = results.filter((r) => !r.ok).length;
  const ok = results.length - failed;
  if (results.length === 0) return { tone: "ok", text: "Nothing applied." };
  if (failed === 0) {
    return { tone: "ok", text: `Applied to ${ok} of ${results.length} AP${results.length === 1 ? "" : "s"}.` };
  }
  if (ok === 0) {
    return { tone: "failed", text: `Failed on all ${results.length} AP${results.length === 1 ? "" : "s"}.` };
  }
  return { tone: "partial", text: `Applied to ${ok} of ${results.length} APs; ${failed} failed.` };
}

/** Banner after saving a profile in use (PUT returns used_by). Null when no AP uses it. */
export function profileDriftBanner(profileName: string, usedBy: number | null | undefined): string | null {
  if (!usedBy || usedBy <= 0) return null;
  const aps = usedBy === 1 ? "1 AP uses" : `${usedBy} APs use`;
  return (
    `Saved "${profileName}". ${aps} this profile; changes are not on the APs until you apply ` +
    `(Assign APs tab).`
  );
}
